/**
 * **演出を詰めるための足場**（Issue #536。索引 #525 の段階 6）。**ページの持ち物**（純関数だけ）。
 *
 * 「テーマを詰める」（`duelEditorGui.js`）は数値のノブだが、**演出は帯が出ている瞬間しか画面に無い**。
 * だから「その演出へ飛ぶ / その区間を繰り返し再生する」が要る（`/fx-samples` の ▶ と同じ）。
 *
 * ここが持つのは**計算だけ**（一覧の作り方・繰り返しの折り返し）。lil-gui も React も知らない。
 */
import sample from '../editorPorts/samples/sampleDuel.json';
import sampleScenes from '../editorPorts/samples/sampleScenes.json';
import { roleTypeId } from '../duelEditor/core/effect';

/**
 * サンプルの試合か、**演出の一覧の場面**（`sampleScenes.json` の `id`。例 `lp_random`）。
 * 通しの試合に出ないイベント（回復など）を撮るときに場面を選ぶ（Issue #585。`/duel-editor?scene=<id>`）。
 * 知らない id は通しの試合。
 */
export function sampleOf(sceneId = null) {
  return (sceneId && (sampleScenes.scenes ?? []).find((x) => x.id === sceneId)) || sample;
}

/**
 * **サンプルの試合**（`editorPorts/samples/sampleDuel.json`。ocgcore のテスト用に録った
 * greedy bot の本物の 1 試合）を、ページが読む形（`loadReplay` の返り値）に合わせる。
 *
 * 実試合を読むと「融合の演出を詰めたい」たびに**融合が出てくる試合を探す**ことになるので、
 * 演出を詰める作業はこちらで回す（設計文書 §4.2）。**core も動画も要らない。**
 */
export function sampleLoaded(sceneId = null) {
  const src = sampleOf(sceneId);
  return {
    // card_info（種類・レベル）も渡す: 疑似イベント material_summon の素材のレベル（数式の ★）が読む（Issue #600）
    state: { events: src.events ?? [], board_steps: src.board_steps ?? null, card_info: src.card_info ?? null },
    replay: { ops: src.ops ?? [] },
  };
}

/** サンプルの開始位置など（`replayHead` は replay の形を前提にするので、ここで直に出す） */
export function sampleHead(sceneId = null) {
  const src = sampleOf(sceneId);
  return {
    startVideoMs: src.start_video_ms ?? 0,
    startLp: src.start_lp ?? 8000,
    firstPlayer: src.first_player ?? 0,
  };
}

/**
 * **演出の一覧**（そのフレームではなく試合ぜんぶ）。
 *
 * @param timeline `editor.timeline`
 * @param effects テーマの演出
 * @returns `[{ event, label, count, clips: [{ id, startMs, endMs }] }]`。**出た数の多い順ではなく宣言の順**
 *   （テーマのファイルの並びと同じにすると探しやすい）
 */
export function effectRows(timeline, effects = []) {
  const items = timeline?.items ?? [];
  // **1 行 = 帯の型 1 つ**（状況 × 役割。motion は `<situation>_motion` の別の行）
  const seen = new Set();
  const rows = [];
  (effects ?? []).forEach((effect) => {
    const type = roleTypeId(effect.situation, effect.role ?? 'effect');
    if (seen.has(type)) return;
    seen.add(type);
    const clips = items
      .filter((it) => it.type === type)
      .map((it) => ({ id: it.id, startMs: it.startMs, endMs: it.endMs }))
      .sort((a, b) => a.startMs - b.startMs);
    const label = effect.label ?? effect.situation;
    rows.push({ event: type, label: effect.role === 'motion' ? `${label}（motion）` : label, count: clips.length, clips });
  });
  return rows;
}

/** 帯の前後に余白を付けた再生範囲（頭の立ち上がりを見たいので前にも少し取る） */
export const PAD_MS = 400;

export function clipRange(clip, { padMs = PAD_MS } = {}) {
  if (!clip) return null;
  const startMs = Math.max(0, (clip.startMs ?? 0) - padMs);
  const endMs = Math.max(startMs + 1, (clip.endMs ?? clip.startMs ?? 0) + padMs);
  return { startMs, endMs };
}

/**
 * **繰り返し再生の折り返し**（純関数）。範囲の終わりを越えたら頭へ戻す時刻を返す（戻さないなら null）。
 * 実際に seek するのは呼ぶ側（ここは時計を持たない）。
 */
export function loopSeek(ms, range) {
  if (!range) return null;
  if (ms < range.startMs || ms >= range.endMs) return range.startMs;
  return null;
}

/** 次の帯（今の位置より後ろで一番近いもの）。無ければ最初へ戻る */
export function nextClip(clips = [], ms = 0) {
  return clips.find((c) => c.startMs > ms) ?? clips[0] ?? null;
}
